import { z } from "zod";
export * from "./rollout-access.js";
export const workflowStates = [
    "draft",
    "submitted",
    "triage",
    "scoring",
    "decision_pending",
];
export const decisionOutcomes = [
    "approved",
    "queued",
    "parked",
    "declined",
];
export const deliveryStates = [
    "planned",
    "in_delivery",
    "paused",
    "completed",
    "closed",
];
export const boardStates = [
    "not_required",
    "required",
    "submitted",
    "decided",
    "withdrawn",
];
export const categories = ["A", "B", "C", "D", "F"];
export const confidentialityLevels = ["standard", "confidential"];
export function normalizeQuarter(value) {
    if (!value?.trim())
        return "Unscheduled";
    const text = value.trim().replace(/\s+/g, " ");
    const match = /^Q([1-4])[\s-]*(20\d{2})(\+)?$/i.exec(text);
    if (match)
        return `Q${match[1]} ${match[2]}${match[3] ?? ""}`;
    const reversed = /^(20\d{2})[\s-]*Q([1-4])(\+)?$/i.exec(text);
    return reversed ? `Q${reversed[2]} ${reversed[1]}${reversed[3] ?? ""}` : text;
}
export function quarterOrder(value) {
    const range = /^Q([1-4])\s*[-–]\s*Q([1-4])\s+(20\d{2})$/i.exec(value.trim());
    if (range && Number(range[2]) >= Number(range[1])) {
        return Number(range[3]) * 4 + Number(range[1]) + 0.25;
    }
    const match = /^Q([1-4]) (20\d{2})(\+)?$/.exec(normalizeQuarter(value));
    return match
        ? Number(match[2]) * 4 + Number(match[1]) + (match[3] ? 0.5 : 0)
        : Infinity;
}
export const scoreInputSchema = z.object({
    reach: z.number().int().min(1).max(5),
    impact: z.number().int().min(1).max(5),
    confidence: z.union([
        z.literal(0.2),
        z.literal(0.5),
        z.literal(0.8),
        z.literal(1),
    ]),
    effort: z.number().int().min(1).max(5),
    strategicMultiplier: z.union([
        z.literal(1),
        z.literal(1.25),
        z.literal(1.75),
        z.literal(2),
        z.literal(3),
    ]),
});
export function calculateRice(input) {
    const parsed = scoreInputSchema.parse(input);
    const base = round1((parsed.reach * parsed.impact * parsed.confidence) / parsed.effort);
    return {
        base,
        adjusted: round1(base * parsed.strategicMultiplier),
    };
}
export function freshnessFromDays(days) {
    if (days <= 7)
        return "fresh";
    if (days <= 21)
        return "watch";
    return "stale";
}
export function freshnessFromDate(lastReviewedAt, now = new Date()) {
    const elapsed = Math.max(0, now.getTime() - lastReviewedAt.getTime());
    const days = Math.floor(elapsed / 86_400_000);
    return { days, status: freshnessFromDays(days) };
}
export const boardTriggerSchema = z.object({
    regulatoryApprovalRequired: z.boolean().default(false),
    materialCapitalCommitment: z.boolean().default(false),
    contractualExclusivity: z.boolean().default(false),
    materialEnterpriseRisk: z.boolean().default(false),
});
export function requiresBoardEscalation(input) {
    const triggers = boardTriggerSchema.parse(input);
    return Object.values(triggers).some(Boolean);
}
export const initiativeInputSchema = z
    .object({
    name: z.string().trim().min(3).max(180),
    description: z.string().trim().min(10).max(10_000),
    expectedOutcome: z.string().trim().max(4000).optional().nullable(),
    category: z.enum(categories),
    strategicDriver: z.string().trim().min(2).max(120),
    team: z.string().trim().min(2).max(120),
    sponsor: z.string().trim().min(2).max(160),
    productOwner: z.string().trim().max(160).optional().nullable(),
    technicalOwner: z.string().trim().max(160).optional().nullable(),
    targetQuarter: z
        .string()
        .trim()
        .max(40)
        .transform((value) => (value ? normalizeQuarter(value) : null))
        .optional()
        .nullable(),
    deadline: z.string().trim().max(80).optional().nullable(),
    estimatedSprints: z.number().int().min(0).max(520).optional().nullable(),
    confidentiality: z.enum(confidentialityLevels).default("standard"),
    score: scoreInputSchema.optional().nullable(),
    boardTriggers: boardTriggerSchema.default({
        regulatoryApprovalRequired: false,
        materialCapitalCommitment: false,
        contractualExclusivity: false,
        materialEnterpriseRisk: false,
    }),
    dependencyIds: z.array(z.string().trim().min(1)).default([]),
})
    .strict();
export const initiativePatchSchema = initiativeInputSchema.partial().extend({
    confidentiality: z.enum(confidentialityLevels).optional(),
    score: scoreInputSchema.nullable().optional(),
    boardTriggers: z
        .object({
        regulatoryApprovalRequired: z.boolean().optional(),
        materialCapitalCommitment: z.boolean().optional(),
        contractualExclusivity: z.boolean().optional(),
        materialEnterpriseRisk: z.boolean().optional(),
    })
        .optional(),
    dependencyIds: z.array(z.string().trim().min(1)).optional(),
    expectedVersion: z.number().int().positive(),
    changeReason: z.string().trim().min(3).max(1_000),
    priorityOverride: z
        .object({
        band: z.enum(["critical", "high", "medium", "low"]),
        reason: z.string().trim().min(10).max(1_000),
    })
        .nullable()
        .optional(),
});
export const transitionSchema = z.object({
    workflowState: z.enum(workflowStates).optional(),
    decisionOutcome: z.enum(decisionOutcomes).nullable().optional(),
    deliveryState: z.enum(deliveryStates).nullable().optional(),
    boardState: z.enum(boardStates).optional(),
    reason: z.string().trim().min(3).max(2_000),
    expectedVersion: z.number().int().positive(),
});
export function scoreBand(adjusted) {
    if (adjusted >= 20)
        return "critical";
    if (adjusted >= 10)
        return "high";
    if (adjusted >= 5)
        return "medium";
    return "low";
}
function round1(value) {
    return Math.round((value + Number.EPSILON) * 10) / 10;
}
